import type {NodeParameters} from "@cm/graph-v3"
import {z} from "zod/v4-mini"
import {TemplateNode} from "#template/template-node/template-node"
import type {TemplateListNode} from "#template/template-list-node/template-list-node"
import type {MaterialAssignmentConstructorParameters, MaterialAssignmentParameters} from "#template/nodes/material-assignment/material-assignment"
import type {EvaluableTemplateNode} from "#template/evaluable-template-node"
import type {
    INodeSceneInteractionsContext,
    IProceduralTemplateContext,
    SceneNodePart,
    ObjectPathPart,
    TemplateNodePart,
    TransformInteraction,
} from "#template/interactions"
import type {Matrix4} from "@cm/math"

export const nodeOwnerMarker = Symbol("NodeOwner")
export const valueMarker = Symbol("Value")
export const sceneInteractiveMarker = Symbol("SceneInteractive")
export const proceduralTemplateMarker = Symbol("ProceduralTemplate")
// Owning containers store the nodes outright (TemplateNodes); reference containers hold node
// references the surrounding switch resolves at runtime (LikesListNode family).
export const templateOwningContainerMarker = Symbol("TemplateOwningContainer")
export const templateReferenceContainerMarker = Symbol("TemplateReferenceContainer")
// Marks an *evaluable FT value* — the abstract FT value bases + factory-generated Input/Output/Switch.
// NOT stamped on Export (a compile-time sink) or Likes (a list container): those are FT *members*
// (see `fundamentalTypeForNodeClass`) but not fundamental-*like* values. `isFundamentalLikeNode` checks this symbol.
export const fundamentalLikeMarker = Symbol("FundamentalLike")
// Marks nodes carrying `MaterialAssignmentParameters` regardless of base class (MaterialAssignment,
// @cm/mattress-nodes' MattressMaterialAssignment) — consumers like the value-slot's material card
// must accept all of them without depending on downstream packages.
export const materialAssignmentLikeMarker = Symbol("MaterialAssignmentLike")
// Marks non-owner nodes whose onFilterActive activates some of their parameters; the variation analysis must treat them as
// activation paths, and the guard in the variation tests fails for any visiting class that carries neither this nor an owner marker.
export const activationVisitorMarker = Symbol("ActivationVisitor")

export interface NodeOwnerLikeNode extends TemplateNode<{nodes: TemplateListNode<TemplateNode>}> {
    readonly [nodeOwnerMarker]: true
}

export interface ValueLikeNode<TValue = unknown> extends TemplateNode<{value: TValue}> {
    readonly [valueMarker]: true
}

// `implements MaterialAssignmentLikeNode` forces carriers to declare the marker getter at compile time.
export interface MaterialAssignmentLikeNode extends TemplateNode<MaterialAssignmentParameters, MaterialAssignmentConstructorParameters> {
    readonly [materialAssignmentLikeMarker]: true
}

export interface ProceduralTemplateNode extends TemplateNode<NodeParameters> {
    readonly [proceduralTemplateMarker]: true
    getParameterNodePartForSceneNodePart(sceneNodePart: SceneNodePart, context: IProceduralTemplateContext): ObjectPathPart | undefined
    getSceneNodePartsForParameterNodePart(parameterNodePart: TemplateNodePart, context: IProceduralTemplateContext): SceneNodePart[]
}

export interface TemplateOwningContainer extends TemplateListNode<TemplateNode> {
    readonly [templateOwningContainerMarker]: true
}

export interface TemplateReferenceContainer extends TemplateListNode<TemplateNode> {
    readonly [templateReferenceContainerMarker]: true
}

export type TemplateContainer = TemplateOwningContainer | TemplateReferenceContainer

export interface SceneInteractiveNode extends TemplateNode<NodeParameters> {
    readonly [sceneInteractiveMarker]: true
    canTransformWith(nodeParts: TemplateNodePart[]): boolean
    canTransformPart(part: TemplateNodePart["part"], context: INodeSceneInteractionsContext): boolean
    beginTransformInteraction?(part: TemplateNodePart["part"], context: INodeSceneInteractionsContext): TransformInteraction | undefined
    getPartTransform(part: TemplateNodePart["part"], context: INodeSceneInteractionsContext): Matrix4
    getPartMaterialAssignment?(part: TemplateNodePart["part"]): TemplateNode<MaterialAssignmentParameters, MaterialAssignmentConstructorParameters> | undefined
    isDropTargetFor(dropSourceNode: TemplateNode, dropTargetPart: TemplateNodePart["part"]): boolean
    handleDroppedSource(dropSourceNode: TemplateNode, dropTargetPart: TemplateNodePart["part"], context: INodeSceneInteractionsContext): void
}

export const hasMarker = (x: unknown, marker: symbol): boolean => x instanceof TemplateNode && (x as unknown as Record<symbol, unknown>)[marker] === true

// Class-level counterpart to `hasMarker`. Markers are declared as prototype getters (enforced by lint), so a
// node class carries them on its prototype chain — queryable from the class itself, without an instance.
export const classHasMarker = (ctor: abstract new (...args: never[]) => unknown, marker: symbol): boolean => marker in (ctor as {prototype: object}).prototype

export const isNodeOwnerLikeNode = (x: unknown): x is NodeOwnerLikeNode => hasMarker(x, nodeOwnerMarker)
export const isValueLikeNode = (x: unknown): x is ValueLikeNode => hasMarker(x, valueMarker)
export const isMaterialAssignmentLikeNode = (x: unknown): x is MaterialAssignmentLikeNode => hasMarker(x, materialAssignmentLikeMarker)
export const isSceneInteractiveNode = (x: unknown): x is SceneInteractiveNode => hasMarker(x, sceneInteractiveMarker)
export const isProceduralTemplateNode = (x: unknown): x is ProceduralTemplateNode => hasMarker(x, proceduralTemplateMarker)
export const isTemplateOwningContainer = (x: unknown): x is TemplateOwningContainer => hasMarker(x, templateOwningContainerMarker)
export const isTemplateReferenceContainer = (x: unknown): x is TemplateReferenceContainer => hasMarker(x, templateReferenceContainerMarker)
export const isTemplateContainer = (x: unknown): x is TemplateContainer => isTemplateOwningContainer(x) || isTemplateReferenceContainer(x)
export const isActivationVisitorNode = (x: unknown): x is TemplateNode => hasMarker(x, activationVisitorMarker)

// `name: string` is so widely shared across template nodes (~96 classes) that a marker would either need
// to be pasted onto every concrete class or onto every abstract base, with no guarantee that subclasses
// without `name` couldn't slip in. Stay with a structural check.
export const isNamedNode = (x: unknown): x is TemplateNode<{name: string}> =>
    x instanceof TemplateNode && typeof (x.parameters as {name?: unknown}).name === "string"

// Base interface for every FT-typed like-node. Forces every per-FT marker interface to carry the
// shared `fundamentalLikeMarker` — a compile-time guard that catches forgetting to add the marker
// when introducing a new FT.
export interface FundamentalLikeNode<R = unknown> extends EvaluableTemplateNode<R> {
    readonly [fundamentalLikeMarker]: true
}

export const isFundamentalLikeNode = (x: unknown): x is FundamentalLikeNode<unknown> => hasMarker(x, fundamentalLikeMarker)
export const isFundamentalLikeNodeClass = (ctor: abstract new (...args: never[]) => unknown): boolean => classHasMarker(ctor, fundamentalLikeMarker)
export const fundamentalLikeNode = z.custom<FundamentalLikeNode<unknown>>(isFundamentalLikeNode)

// Distributive so `LikeNodeOf<string | StringLikeNode>` reduces to `StringLikeNode` at every
// instantiation (a plain intersection would stay opaque).
export type LikeNodeOf<TLike> = TLike extends FundamentalLikeNode<unknown> ? TLike : never
export type LikeRawOf<TLike> = Exclude<TLike, FundamentalLikeNode<unknown>>

// FT-style "like" marker bundle. `TNode` is the like-node interface (must extend
// `FundamentalLikeNode`); `TRaw` is the raw companion (if any). The caller declares the symbol +
// interface at module scope — TS needs the symbol to be `unique symbol` for `[marker]: true` to be
// a valid type key — and `createFundamentalMarker` then produces the predicates and zod schemas.
export interface LikeMarker<TNode extends FundamentalLikeNode<unknown>, TRaw = never> {
    readonly marker: symbol
    readonly isLikeNode: (x: unknown) => x is TNode
    readonly isLikeNodeClass: (ctor: abstract new (...args: never[]) => unknown) => boolean
    readonly likeNode: z.ZodMiniType<TNode, TNode>
    readonly isLike: (x: unknown) => x is TNode | TRaw
    readonly like: z.ZodMiniType<TNode | TRaw, TNode | TRaw>
}

export function createFundamentalMarker<TNode extends FundamentalLikeNode<unknown>, TRaw = never>(
    marker: symbol,
    opts?: {rawSchema: z.ZodMiniType<TRaw, TRaw>; acceptsRaw: (x: unknown) => x is TRaw},
): LikeMarker<TNode, TRaw> {
    const isLikeNode = (x: unknown): x is TNode => hasMarker(x, marker)
    const isLikeNodeClass = (ctor: abstract new (...args: never[]) => unknown): boolean => classHasMarker(ctor, marker)
    const likeNode = z.custom<TNode>(isLikeNode)
    const isLike: (x: unknown) => x is TNode | TRaw = opts ? (x): x is TNode | TRaw => opts.acceptsRaw(x) || isLikeNode(x) : isLikeNode
    const like: z.ZodMiniType<TNode | TRaw, TNode | TRaw> = opts ? z.union([opts.rawSchema, likeNode]) : (likeNode as z.ZodMiniType<TNode | TRaw, TNode | TRaw>)
    return {marker, isLikeNode, isLikeNodeClass, likeNode, isLike, like}
}
